import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Message, MessageDraft } from '../models/message';
import { useSendMessageMutation } from './useChatQuery';
import { showErrorBanner } from '../../../Components/ErrorBanner';
import { SendMessageResult } from '../services/chatService';
import { generateClientTurnId } from '../services/clientTurnId';
import {
   addProviderErrorPlaceholder,
   captureTurnBaseline,
   createOptimisticUserMessage,
   findUserMessageForTurn,
   isTurnAnswered,
   markTurnFailed,
   markTurnStatus,
   prepareTurnRetry,
   resolveRetryTarget,
   TurnBaseline
} from '../services/reconcile';
import { DraftsByTurn, keepDraftsOf, selectVisibleDrafts, staleDraftTurns } from '../services/drafts';
import { ChatLanguage, getChatStrings } from '../i18n';

/** After this long without an answer the typing indicator becomes a ProviderError bubble with Retry. */
export const ANSWER_SOFT_TIMEOUT_MS = 20000;

interface UseMessageHandlingProps {
   messages: Message[];
   setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
   /** Answer drafts by turn (filled from SignalR); never part of `messages`. */
   drafts: DraftsByTurn;
   setDrafts: React.Dispatch<React.SetStateAction<DraftsByTurn>>;
   isConnected: boolean;
   uiLanguage: ChatLanguage;
   getLanguageHint: (isFirstMessage: boolean) => ChatLanguage | undefined;
}

/** One send attempt of a turn. `clientTurnId` is the idempotency key; a Retry reuses it with the same text. */
interface Turn {
   clientTurnId: string;
   content: string;
   language?: string;
}

export const useMessageHandling = ({ messages, setMessages, drafts, setDrafts, isConnected, uiLanguage, getLanguageHint }: UseMessageHandlingProps) => {
   const [input, setInput] = useState('');
   /** clientTurnIds waiting for their assistant message (drives the typing indicator). */
   const [awaitingTurns, setAwaitingTurns] = useState<string[]>([]);
   const [showVerification, setShowVerification] = useState(false);
   const sendMessageMutation = useSendMessageMutation();

   const messagesRef = useRef(messages);
   messagesRef.current = messages;
   const stringsRef = useRef(getChatStrings(uiLanguage));
   stringsRef.current = getChatStrings(uiLanguage);

   const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
   const baselinesRef = useRef(new Map<string, TurnBaseline>());
   /** Turns whose POST is still running (a Retry then just waits again). */
   const inFlightRef = useRef(new Set<string>());
   const verificationTurnRef = useRef<string | null>(null);

   const stopAwaiting = useCallback((turnId: string) => {
      const timer = timersRef.current.get(turnId);
      if (timer) clearTimeout(timer);
      timersRef.current.delete(turnId);
      baselinesRef.current.delete(turnId);
      setAwaitingTurns((prev) => (prev.includes(turnId) ? prev.filter((t) => t !== turnId) : prev));
   }, []);

   const startAwaiting = useCallback(
      (turnId: string, baseline: TurnBaseline = captureTurnBaseline(messagesRef.current, turnId)) => {
         const existing = timersRef.current.get(turnId);
         if (existing) clearTimeout(existing);

         baselinesRef.current.set(turnId, baseline);
         setAwaitingTurns((prev) => (prev.includes(turnId) ? prev : [...prev, turnId]));

         timersRef.current.set(
            turnId,
            setTimeout(() => {
               const baseline = baselinesRef.current.get(turnId);
               timersRef.current.delete(turnId);
               baselinesRef.current.delete(turnId);
               setAwaitingTurns((prev) => prev.filter((t) => t !== turnId));
               setMessages((prev) => (isTurnAnswered(prev, turnId, baseline) ? prev : addProviderErrorPlaceholder(prev, turnId)));
            }, ANSWER_SOFT_TIMEOUT_MS)
         );
      },
      [setMessages]
   );

   // The typing indicator stays on until the assistant message for the turn is on screen.
   useEffect(() => {
      awaitingTurns.forEach((turnId) => {
         if (isTurnAnswered(messages, turnId, baselinesRef.current.get(turnId))) stopAwaiting(turnId);
      });
   }, [messages, awaitingTurns, stopAwaiting]);

   // A draft lasts only while its turn waits: the answer, a failed send or the soft timeout ends it, and a draft that
   // arrives after them is dropped.
   useEffect(() => {
      if (staleDraftTurns(drafts, awaitingTurns).length > 0) setDrafts((prev) => keepDraftsOf(prev, awaitingTurns));
   }, [drafts, awaitingTurns, setDrafts]);

   // A draft is never the answer (the typing indicator and the soft timeout run on), and is hidden the render its
   // answer is on screen.
   const visibleDrafts = useMemo<MessageDraft[]>(
      () => selectVisibleDrafts(drafts, awaitingTurns, (turnKey) => isTurnAnswered(messages, turnKey, baselinesRef.current.get(turnKey))),
      [drafts, awaitingTurns, messages]
   );

   // Clear pending timers on unmount.
   useEffect(() => {
      const timers = timersRef.current;
      return () => {
         timers.forEach((timer) => clearTimeout(timer));
         timers.clear();
      };
   }, []);

   const { mutateAsync: sendTurnRequest } = sendMessageMutation;

   const dispatchTurn = useCallback(
      async ({ clientTurnId: turnId, content, language }: Turn) => {
         // Assistant messages already shown for this turn (e.g. a server ProviderError
         // being retried) do not count as the answer to this attempt.
         const baseline = captureTurnBaseline(messagesRef.current, turnId);
         startAwaiting(turnId, baseline);
         inFlightRef.current.add(turnId);

         let result: SendMessageResult;
         try {
            result = await sendTurnRequest({ content, clientTurnId: turnId, ...(language ? { language } : {}) });
         } catch {
            result = { status: 'failed', kind: 'network' };
         } finally {
            inFlightRef.current.delete(turnId);
         }

         const strings = stringsRef.current;
         switch (result.status) {
            case 'sent':
               setMessages((prev) => markTurnStatus(prev, turnId, 'sent'));
               break;
            case 'in-flight':
               // An earlier request for this very turn is still running; its outcome decides.
               break;
            case 'conflict':
               stopAwaiting(turnId);
               setMessages((prev) => markTurnFailed(prev, turnId, 'conflict'));
               showErrorBanner(strings.conflictBanner);
               break;
            case 'failed': {
               // If a real answer for this attempt already arrived, the server clearly got the message.
               const withoutPlaceholders = messagesRef.current.filter((m) => !(m.sender === 'bot' && m.isOptimistic));
               if (isTurnAnswered(withoutPlaceholders, turnId, baseline)) {
                  setMessages((prev) => markTurnStatus(prev, turnId, 'sent'));
                  break;
               }
               const kind = result.kind;
               stopAwaiting(turnId);
               setMessages((prev) => markTurnFailed(prev, turnId, kind));
               showErrorBanner(result.message || strings.sendFailedBanner);
               if (result.verificationRequired) {
                  verificationTurnRef.current = turnId;
                  setShowVerification(true);
               }
               break;
            }
         }
      },
      [sendTurnRequest, setMessages, startAwaiting, stopAwaiting]
   );

   /** Starts a new turn for the guest's words: their message on screen at once, then the send. */
   const sendContent = useCallback(
      (content: string) => {
         const isFirstMessage = !messagesRef.current.some((m) => m.sender === 'user');
         const language = getLanguageHint(isFirstMessage);
         const clientTurnId = generateClientTurnId();

         const userMessage = createOptimisticUserMessage({ text: content, clientTurnId, language });
         messagesRef.current = [...messagesRef.current, userMessage];
         setMessages((prev) => [...prev, userMessage]);

         void dispatchTurn({ clientTurnId, content, language });
      },
      [getLanguageHint, setMessages, dispatchTurn]
   );

   const handleSend = useCallback(() => {
      const content = input.trim();
      if (!content || !isConnected) return;
      setInput('');
      sendContent(content);
   }, [input, isConnected, sendContent]);

   /**
    * Sends words the guest said, as if typed and sent. Without a connection they wait in the field instead, so what
    * was said is not lost: the guest sends it once the chat is back.
    */
   const handleSendText = useCallback(
      (text: string) => {
         const content = text.trim();
         if (!content) return;
         if (!isConnected) {
            setInput(content);
            return;
         }
         sendContent(content);
      },
      [isConnected, sendContent]
   );

   /**
    * Re-sends the turn behind a failed guest message or a ProviderError bubble:
    * the ORIGINAL guest text with the SAME clientTurnId (never a new id). The
    * typing indicator then waits for a newer assistant message of that turn,
    * or the soft timeout.
    */
   const retryMessage = useCallback(
      (messageId: string) => {
         const target = resolveRetryTarget(messagesRef.current, messageId);
         if (!target) return;

         const { clientTurnId } = target;
         const next = prepareTurnRetry(messagesRef.current, clientTurnId);
         messagesRef.current = next;
         setMessages((prev) => prepareTurnRetry(prev, clientTurnId));

         if (inFlightRef.current.has(clientTurnId)) {
            // The original request is still running: just wait for it again.
            startAwaiting(clientTurnId);
            return;
         }
         void dispatchTurn({ clientTurnId, content: target.text, language: target.language });
      },
      [dispatchTurn, setMessages, startAwaiting]
   );

   const handleCustomerVerification = useCallback(
      async (username: string, roomNumber: string) => {
         try {
            // TODO: Add API call to verify customer with username and room number
            setShowVerification(false);
            // Retry the turn that required verification (same clientTurnId)
            const clientTurnId = verificationTurnRef.current;
            verificationTurnRef.current = null;
            if (clientTurnId) {
               const userMessage = findUserMessageForTurn(messagesRef.current, clientTurnId);
               if (userMessage?.id) retryMessage(userMessage.id);
            }
         } catch (error) {
            showErrorBanner('Failed to verify customer details. Please try again.');
         }
      },
      [retryMessage]
   );

   return {
      input,
      setInput,
      isBotTyping: awaitingTurns.length > 0,
      // Still true the render the answer lands (its draft is hidden, its turn not yet done), so the line never flips back
      isBotWriting: awaitingTurns.some((turnKey) => !!drafts[turnKey]),
      awaitingTurns,
      visibleDrafts,
      handleSend,
      handleSendText,
      retryMessage,
      showVerification,
      setShowVerification,
      handleCustomerVerification
   };
};
